import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { imageVariant, resolveMediaUrl } from "@/lib/mediaUrl";
import { isHlsSrc, posterFor, attachHls } from "@/lib/videoSource";
import { useFeedAttachSlot, fmtDur } from "@/components/VideoPlayer";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { fxStyle } from "@/lib/mediaFx";

// Mosaïque d'un album mêlant vidéos (et photos) dans le fil — même grille que les photos (2 côte à côte,
// 3 = 1 grande + 2 petites, 4 = 2×2, 5+ = « +N »). Une vignette vidéo joue en MUET dès qu'elle est à l'écran
// (budget de décodeurs du fil partagé), vidéo entière sur son affiche floutée, badge durée ; le son est dans la
// visionneuse (tap).
const cardWidth = () => Math.min(typeof window !== "undefined" ? window.innerWidth : 640, 640);

export const AlbumVideoTile = ({ m, onOpen, testId, poster: posterProp, className = "" }) => {
  const ref = useRef(null);
  const visibleRef = useRef(false);
  const [dur, setDur] = useState(Number(m.duration) > 0 ? Number(m.duration) : null);
  const attached = useFeedAttachSlot(ref);
  const hls = isHlsSrc(m.url);
  const poster = posterProp || posterFor(m.url, m.poster_url ? imageVariant(m.poster_url, 480) : m.poster_url) || NEUTRAL_POSTER;

  useEffect(() => {
    const el = ref.current;
    if (!el || !attached) return undefined;
    if (hls) return attachHls(el, m.url, { autoPlay: () => visibleRef.current });
    el.src = resolveMediaUrl(m.url);
    if (visibleRef.current) el.play().catch(() => {});
    return () => { try { el.removeAttribute("src"); el.load(); } catch { /* noop */ } };
  }, [m.url, hls, attached]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      visibleRef.current = !!e.isIntersecting;
      if (e.isIntersecting) el.play().catch(() => {}); else el.pause();
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <button type="button" onClick={onOpen} className={`relative block w-full h-full overflow-hidden bg-black ${className}`} data-testid={testId} data-kind="video">
      <div className="absolute inset-0 pointer-events-none" aria-hidden
        style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(24px)", transform: "scale(1.2)", opacity: 0.75 }} />
      <video ref={ref} poster={poster} muted loop playsInline preload="metadata"
        onLoadedMetadata={(e) => { const d = e.target.duration; if (isFinite(d) && d > 0) setDur(d); }}
        className="relative z-[1] w-full h-full object-contain pointer-events-none" style={fxStyle(m.fx)} data-testid={`${testId}-video`} />
      <span className="absolute top-2 left-2 z-[2] h-7 w-7 rounded-full grid place-items-center text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.55)" }} aria-hidden>
        <Play size={13} className="ml-0.5" />
      </span>
      {dur != null && (
        <span className="absolute bottom-2 left-2 z-[2] px-1.5 py-0.5 rounded text-[11px] font-semibold text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.72)" }} data-testid={`${testId}-duration`}>
          {fmtDur(dur)}
        </span>
      )}
    </button>
  );
};

const ImageTile = ({ m, onOpen, testId }) => (
  <button type="button" onClick={onOpen} className="relative block w-full h-full overflow-hidden bg-black/5" data-testid={testId} data-kind="image">
    <img src={imageVariant(m.url, cardWidth() / 2)} loading="lazy" decoding="async" alt="" className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.04]" />
  </button>
);

const Tile = ({ m, i, onOpen, tid, overlay, className = "" }) => (
  <div className={`relative ${className}`} data-testid={`${tid}-cell-${i}`}>
    {m.type === "video" ? <AlbumVideoTile m={m} onOpen={() => onOpen(i)} testId={`${tid}-tile-${i}`} /> : <ImageTile m={m} onOpen={() => onOpen(i)} testId={`${tid}-tile-${i}`} />}
    {overlay != null && (
      <button type="button" onClick={() => onOpen(i)} className="absolute inset-0 z-[4] bg-black/55 grid place-items-center" data-testid={`${tid}-more`}>
        <span className="text-white text-2xl font-bold font-display">+{overlay}</span>
      </button>
    )}
  </div>
);

export const AlbumMosaic = ({ post, items = [], onOpen, className = "" }) => {
  const n = items.length;
  if (n === 0) return null;
  const tid = `post-album-${post?.id}`;
  const open = (i) => onOpen?.(i);
  let grid;
  if (n === 1) {
    grid = <Tile m={items[0]} i={0} onOpen={open} tid={tid} className="aspect-[4/5]" />;
  } else if (n === 2) {
    grid = (
      <div className="grid grid-cols-2 gap-1">
        <Tile m={items[0]} i={0} onOpen={open} tid={tid} className="aspect-[3/4]" />
        <Tile m={items[1]} i={1} onOpen={open} tid={tid} className="aspect-[3/4]" />
      </div>
    );
  } else if (n === 3) {
    grid = (
      <div className="grid grid-cols-2 grid-rows-2 gap-1 h-[340px] sm:h-[420px]">
        <Tile m={items[0]} i={0} onOpen={open} tid={tid} className="row-span-2 h-full" />
        <Tile m={items[1]} i={1} onOpen={open} tid={tid} className="h-full min-h-0" />
        <Tile m={items[2]} i={2} onOpen={open} tid={tid} className="h-full min-h-0" />
      </div>
    );
  } else if (n === 4) {
    grid = (
      <div className="grid grid-cols-2 gap-1">
        {items.map((m, i) => <Tile key={i} m={m} i={i} onOpen={open} tid={tid} className="aspect-square" />)}
      </div>
    );
  } else {
    grid = (
      <div className="grid grid-cols-6 gap-1">
        <Tile m={items[0]} i={0} onOpen={open} tid={tid} className="col-span-3 aspect-[16/10]" />
        <Tile m={items[1]} i={1} onOpen={open} tid={tid} className="col-span-3 aspect-[16/10]" />
        <Tile m={items[2]} i={2} onOpen={open} tid={tid} className="col-span-2 aspect-square" />
        <Tile m={items[3]} i={3} onOpen={open} tid={tid} className="col-span-2 aspect-square" />
        <Tile m={items[4]} i={4} onOpen={open} tid={tid} className="col-span-2 aspect-square" overlay={n > 5 ? n - 5 : null} />
      </div>
    );
  }
  return (
    <div className={`mt-3 rounded-xl overflow-hidden ${className}`} style={{ border: "1px solid var(--yalola-border)" }} data-testid={tid} data-count={n}>
      {grid}
    </div>
  );
};

export default AlbumMosaic;
